import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { toast } from "sonner";

import { BaseForm, FormInput, FormToggle } from "components/BaseForm";
import { Spinner } from "@/components/ui/spinner";
import ProductService from "services/Product";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  list_price: z
    .string()
    .min(1, "List Price is required")
    .regex(
      /^\d+(\.\d{1,2})?$/,
      "List Price must be a number with at most 2 decimal places"
    ),
  enabled: z.boolean(),
});

function ProductForm({ model, onSuccess }) {
  const { data: product } = useQuery({
    queryKey: ["product", model?.id],
    queryFn: () => ProductService.get(model.id),
    enabled: !!model?.id,
  });

  const initialValues = useMemo(() => {
    if (!model?.id) {
      return { name: "", list_price: "0", enabled: true };
    }
    return (
      product && {
        name: product.name,
        list_price: String(product.list_price ?? ""),
        enabled: product.enabled,
      }
    );
  }, [model?.id, product]);

  const handleSubmit = async values => {
    const data = {
      name: values.name,
      list_price: parseFloat(values.list_price),
      enabled: values.enabled,
    };
    if (model?.id) {
      await ProductService.update(model.id, data);
    } else {
      await ProductService.create(data);
    }
    toast.success("Product saved");
  };

  if (!initialValues) {
    return <Spinner />;
  }

  return (
    <BaseForm
      schema={schema}
      defaultValues={initialValues}
      onSubmit={handleSubmit}
      onSuccess={onSuccess}
    >
      <FormInput name="name" label="Name" required />
      <FormInput name="list_price" label="List Price" type="number" required />
      <FormToggle name="enabled" label="Enabled" />
    </BaseForm>
  );
}

export default ProductForm;
